import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Download, Share2, RefreshCw, Trophy, Users, Zap, Star, Bell, BellOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BADGE_COLORS = {
  diamond:  { bg: '#E8F4FD', text: '#1E6FA1', border: '#7BBFEA', label: 'Diamond' },
  platinum: { bg: '#EEF2FF', text: '#4C51BF', border: '#A5B4FC', label: 'Platinum' },
  gold:     { bg: '#FEF9EC', text: '#B7791F', border: '#F6D860', label: 'Gold' },
  silver:   { bg: '#F4F4F5', text: '#71717A', border: '#D4D4D8', label: 'Silver' },
  bronze:   { bg: '#FEF2E8', text: '#C05621', border: '#F6AD55', label: 'Bronze' },
  starter:  { bg: '#F6F1E9', text: '#8A9A8B', border: '#8A9A8B', label: 'Starter' },
};

const CREDIT_REWARDS_DISPLAY = [
  { credits: 100,  reward: 'Homepage Category Boost',   emoji: '🚀' },
  { credits: 250,  reward: 'Instagram Feature',          emoji: '📸' },
  { credits: 500,  reward: 'Push Notification Blast',   emoji: '🔔' },
  { credits: 750,  reward: 'Featured Banner',            emoji: '⭐' },
  { credits: 1000, reward: 'Premium Placement 7 Days',  emoji: '💎' },
];

export default function GrowthNetworkPage() {
  const [ref, setRef] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [refRes, rankRes, notifRes] = await Promise.all([
        fetch(`${API}/businesses/referral`, { credentials: 'include' }),
        fetch(`${API}/growth/rankings`),
        fetch(`${API}/businesses/referral/notifications`, { credentials: 'include' }),
      ]);
      if (refRes.ok) setRef(await refRes.json());
      if (rankRes.ok) setRankings(await rankRes.json());
      if (notifRes.ok) setNotifs(await notifRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyReferralLink = async () => {
    if (!ref?.referral_url) return;
    await navigator.clipboard.writeText(ref.referral_url);
    setCopying(true);
    setTimeout(() => setCopying(false), 2500);
  };

  const downloadQR = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/businesses/referral/qr.png`, { credentials: 'include' });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'perkly-referral-qr.png'; a.click();
      URL.revokeObjectURL(url);
    } catch {}
    setDownloading(false);
  };

  const markRead = async () => {
    await fetch(`${API}/businesses/referral/notifications/read`, { method: 'POST', credentials: 'include' });
    setNotifs(notifs.map(n => ({ ...n, read: true })));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!ref) return (
    <div className="text-center py-12">
      <p className="text-[#2B2B2B]/50 text-sm">Growth Network data not found. Contact support.</p>
    </div>
  );

  const badgeStyle = BADGE_COLORS[ref.badge_tier || 'starter'] || BADGE_COLORS.starter;
  const nextBadge  = ref.next_badge;
  const progress   = nextBadge?.required ? Math.min(100, (ref.total_members / nextBadge.required) * 100) : 100;
  const unreadCount = notifs.filter(n => !n.read).length;

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'qr',       label: 'QR & Kit' },
    { id: 'rankings', label: 'Rankings' },
    { id: 'rewards',  label: 'Rewards' },
    { id: 'notifs',   label: `Notifications${unreadCount ? ` (${unreadCount})` : ''}` },
  ];

  return (
    <div data-testid="growth-network-panel">
      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-2xl p-1.5 border border-[#8A9A8B]/10 mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            data-testid={`growth-tab-${t.id}`}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${activeTab === t.id ? 'bg-[#2B2B2B] text-[#F6F1E9]' : 'text-[#2B2B2B]/60 hover:text-[#2B2B2B]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Badge card */}
          <div className="rounded-3xl p-6 border-2" style={{ backgroundColor: badgeStyle.bg, borderColor: badgeStyle.border }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: badgeStyle.text }}>Current Badge</p>
                <div className="flex items-center gap-2">
                  <span className="text-3xl">{ref.badge_emoji}</span>
                  <h2 className="text-xl font-bold" style={{ color: badgeStyle.text, fontFamily: 'Playfair Display, serif' }}>{ref.badge}</h2>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold" style={{ color: badgeStyle.text }}>Growth Credits</p>
                <p className="text-3xl font-bold" style={{ color: badgeStyle.text, fontFamily: 'Playfair Display, serif' }}>{ref.total_credits.toLocaleString()}</p>
              </div>
            </div>
            {nextBadge?.name && (
              <div className="mt-4">
                <div className="flex justify-between text-xs mb-1.5" style={{ color: badgeStyle.text }}>
                  <span>{ref.total_members} members</span>
                  <span>{nextBadge.remaining} more to unlock {nextBadge.emoji} {nextBadge.name}</span>
                </div>
                <div className="h-2.5 rounded-full bg-white/40">
                  <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: badgeStyle.text }} />
                </div>
              </div>
            )}
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Members Introduced', value: ref.total_members, sub: `${ref.monthly_members} this month`, icon: Users, color: '#C8A97E' },
              { label: 'QR Scans', value: ref.total_scans, sub: `${ref.monthly_scans} this month`, icon: Star, color: '#8A9A8B' },
              { label: 'Growth Credits', value: ref.total_credits, sub: '10 per member', icon: Zap, color: '#5DBB63' },
              { label: 'Your Rank', value: `#${rankings.findIndex(r => r.business_name && ref.referral_url?.includes(ref.referral_slug)) + 1 || '—'}`, sub: `of ${rankings.length}`, icon: Trophy, color: '#A07BC8' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-2xl p-4 border border-[#8A9A8B]/10">
                <div className="w-8 h-8 rounded-full flex items-center justify-center mb-2" style={{ background: s.color + '18' }}>
                  <s.icon size={16} style={{ color: s.color }} />
                </div>
                <p className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>{s.value}</p>
                <p className="text-xs text-[#2B2B2B]/50">{s.label}</p>
                <p className="text-xs text-[#8A9A8B] mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Referral URL */}
          <div className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10">
            <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] mb-2">Your Referral Link</p>
            <div className="flex items-center gap-2">
              <input readOnly value={ref.referral_url} className="perkly-input flex-1 text-sm font-mono" />
              <button onClick={copyReferralLink} className={`flex-shrink-0 px-4 py-2.5 rounded-full text-xs font-bold transition-all ${copying ? 'bg-green-100 text-green-700' : 'bg-[#2B2B2B] text-white hover:bg-[#C8A97E]'}`}>
                {copying ? '✓ Copied!' : <><Share2 size={13} /> Copy</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR & MARKETING KIT */}
      {activeTab === 'qr' && (
        <div className="space-y-5">
          <div className="bg-white rounded-3xl p-7 border border-[#8A9A8B]/10 text-center">
            <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] mb-4">Your Referral QR Code</p>
            {ref.qr_base64 && (
              <img src={ref.qr_base64} alt="Referral QR" className="w-48 h-48 mx-auto rounded-2xl border border-[#8A9A8B]/15 p-2" />
            )}
            <p className="text-xs text-[#2B2B2B]/50 mt-3 mb-5">
              Place this on receipts, menus, windows, bags, and tables.<br />Every scan brings a new member — you earn 10 credits each time.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button onClick={downloadQR} disabled={downloading} data-testid="download-qr-btn"
                className="flex items-center gap-2 btn-primary text-sm px-6 py-3 disabled:opacity-60">
                {downloading ? 'Downloading...' : <><Download size={14} /> Download QR PNG</>}
              </button>
              <Link to={`/growth/kit?slug=${ref.referral_slug}`} target="_blank"
                className="flex items-center gap-2 btn-secondary text-sm px-6 py-3">
                🖨️ Print Marketing Kit
              </Link>
            </div>
          </div>

          {/* Placement ideas */}
          <div className="bg-[#F6F1E9] rounded-2xl p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] mb-3">Where to Place Your QR</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {['Receipt / Bill', 'Menu / Table Tent', 'Window Sticker', 'Takeout Bag', 'Counter Display', 'Instagram Story', 'Pizza Box', 'Coffee Cup', 'Uber Eats Order'].map(p => (
                <div key={p} className="bg-white rounded-xl px-3 py-2 text-xs text-[#2B2B2B]/70 font-semibold flex items-center gap-1.5">
                  <span className="text-[#C8A97E]">✓</span> {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* RANKINGS */}
      {activeTab === 'rankings' && (
        <div>
          <p className="text-xs text-[#2B2B2B]/50 mb-5">Live rankings — updates instantly when a new member joins from any business QR.</p>
          <div className="space-y-3">
            {rankings.slice(0, 20).map((r, i) => (
              <div key={r.business_name || r.rank} className={`bg-white rounded-2xl p-4 border flex items-center gap-4 ${i === 0 ? 'border-[#F6D860]' : i === 1 ? 'border-[#D4D4D8]' : i === 2 ? 'border-[#F6AD55]' : 'border-[#8A9A8B]/10'}`}>
                <span className="text-2xl font-bold w-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                </span>
                {r.logo_url && <img src={r.logo_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" onError={e => { e.target.style.display='none'; }} />}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#2B2B2B]">{r.business_name}</p>
                  <p className="text-xs text-[#8A9A8B]">{r.badge_emoji} {r.badge} · {r.city_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#C8A97E]">{r.total_members}</p>
                  <p className="text-xs text-[#8A9A8B]">members</p>
                </div>
              </div>
            ))}
            {rankings.length === 0 && <p className="text-center text-[#2B2B2B]/40 py-8">No rankings yet. Be the first!</p>}
          </div>
        </div>
      )}

      {/* REWARDS */}
      {activeTab === 'rewards' && (
        <div className="space-y-4">
          <div className="bg-[#2B2B2B] rounded-2xl p-5 flex items-center justify-between">
            <div>
              <p className="text-white/60 text-xs">Available Credits</p>
              <p className="text-3xl font-bold text-[#C8A97E]" style={{ fontFamily: 'Playfair Display, serif' }}>{ref.total_credits.toLocaleString()}</p>
            </div>
            <Zap size={28} className="text-[#C8A97E]" />
          </div>
          <p className="text-xs text-[#2B2B2B]/55">Redeem your Growth Credits for promotional benefits. Contact support@useperkly.com or your account manager to redeem.</p>
          <div className="space-y-3">
            {CREDIT_REWARDS_DISPLAY.map(r => (
              <div key={r.credits} className={`bg-white rounded-2xl p-4 border flex items-center gap-4 ${ref.total_credits >= r.credits ? 'border-[#C8A97E]/40' : 'border-[#8A9A8B]/12 opacity-60'}`}>
                <span className="text-2xl">{r.emoji}</span>
                <div className="flex-1">
                  <p className="font-bold text-sm text-[#2B2B2B]">{r.reward}</p>
                  <p className="text-xs text-[#8A9A8B]">{r.credits} credits required</p>
                </div>
                {ref.total_credits >= r.credits
                  ? <span className="text-xs font-bold text-[#C8A97E] bg-[#C8A97E]/12 px-3 py-1 rounded-full">Unlocked</span>
                  : <span className="text-xs text-[#8A9A8B]">{r.credits - ref.total_credits} more</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NOTIFICATIONS */}
      {activeTab === 'notifs' && (
        <div>
          {unreadCount > 0 && (
            <button onClick={markRead} className="text-xs text-[#C8A97E] underline mb-4 block">Mark all as read</button>
          )}
          <div className="space-y-2">
            {notifs.slice(0, 30).map((n) => (
              <div key={`${n.created_at}-${n.message}`} className={`rounded-2xl p-4 border flex items-start gap-3 ${!n.read ? 'bg-[#C8A97E]/8 border-[#C8A97E]/30' : 'bg-white border-[#8A9A8B]/10'}`}>
                <span className="text-xl flex-shrink-0">{n.type === 'badge_unlocked' ? '🏆' : '🎉'}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#2B2B2B]">{n.message}</p>
                  {n.credits && <p className="text-xs text-[#C8A97E] font-bold mt-0.5">{n.credits}</p>}
                  <p className="text-xs text-[#8A9A8B] mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                </div>
                {!n.read && <div className="w-2 h-2 rounded-full bg-[#C8A97E] flex-shrink-0 mt-1" />}
              </div>
            ))}
            {notifs.length === 0 && <p className="text-center text-[#2B2B2B]/40 py-8">No notifications yet. Start sharing your QR!</p>}
          </div>
        </div>
      )}
    </div>
  );
}
